import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import {
  ArrowLeft,
  Download,
  Edit3,
  Eye,
  FileUp,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
  Target,
  Clock3,
  CheckCircle2,
} from "lucide-react";
import {
  createCalidadObjetivo,
  createObjetivoEtapa,
  deleteCalidadObjetivo,
  deleteObjetivoAnexo,
  deleteObjetivoEtapa,
  fetchObjetivoCloneSource,
  objetivoAnexoDownloadUrl,
  updateCalidadObjetivo,
  updateObjetivoEtapa,
  uploadObjetivoAnexos,
} from "../api/objetivos";
import { useCalidadObjetivos } from "../hooks/useCalidadObjetivos";
import { Iso900114001ConfirmDialog } from "../../../components/shared/Iso900114001ConfirmDialog";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import {
  createEmptyEtapaForm,
  createEmptyObjetivoForm,
  defaultObjetivoFilters,
  mapCloneSourceToForm,
  mapEtapaFormToPayload,
  mapEtapaToForm,
  mapObjetivoFormToPayload,
  mapObjetivoToForm,
  type CalidadObjetivo,
  type CalidadObjetivoCloneSource,
  type CalidadObjetivoEtapa,
  type CalidadObjetivoEtapaFormValues,
  type CalidadObjetivoFilters,
  type CalidadObjetivoFormValues,
} from "../types/objetivos";

type Props = {
  onBack: () => void;
};

type ObjetivoFormState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; row: CalidadObjetivo | null };

type EtapaFormState =
  | { open: false }
  | { open: true; mode: "create" | "edit"; objetivo: CalidadObjetivo; etapa: CalidadObjetivoEtapa | null };

type DeleteState =
  | { open: false }
  | { open: true; type: "objetivo" | "etapa" | "anexo"; title: string; description: string; objetivo: CalidadObjetivo; etapa?: CalidadObjetivoEtapa; anexoId?: number };

export function CalidadObjetivosSection({ onBack }: Props) {
  const [filters, setFilters] = useState<CalidadObjetivoFilters>(defaultObjetivoFilters);
  const { objetivos, meta, loading, error, reload } = useCalidadObjetivos(filters);
  const [fichaObjetivoId, setFichaObjetivoId] = useState<number | null>(null);
  const [formState, setFormState] = useState<ObjetivoFormState>({ open: false });
  const [etapaFormState, setEtapaFormState] = useState<EtapaFormState>({ open: false });
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cloneSources, setCloneSources] = useState<CalidadObjetivoCloneSource[]>([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);

  const fichaObjetivo = useMemo(
    () => objetivos.find((objetivo) => objetivo.id === fichaObjetivoId) ?? null,
    [objetivos, fichaObjetivoId],
  );

  const overviewCounts = useMemo(
    () => ({
      abiertos: objetivos.filter((objetivo) => !objetivo.cerrado).length,
      cerrados: objetivos.filter((objetivo) => objetivo.cerrado).length,
    }),
    [objetivos],
  );

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const set = new Set<number>([current, ...meta.years]);
    return Array.from(set).sort((a, b) => b - a);
  }, [meta.years]);

  const loadCloneSourcesForYear = async (targetYear: number) => {
    if (!Number.isFinite(targetYear) || targetYear < 2000 || targetYear > 2100) {
      setCloneSources([]);
      return;
    }

    setCloneLoading(true);
    try {
      setCloneSources(await fetchObjetivoCloneSource(targetYear));
    } finally {
      setCloneLoading(false);
    }
  };

  const openCreate = async () => {
    setFormError(null);
    setCloneSources([]);
    setFormState({ open: true, mode: "create", row: null });
    const targetYear = Number.parseInt(filters.anio || String(new Date().getFullYear()), 10);
    await loadCloneSourcesForYear(targetYear);
  };

  const handleSubmitObjetivo = async (values: CalidadObjetivoFormValues) => {
    if (!formState.open) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const payload = mapObjetivoFormToPayload(values);
      if (formState.mode === "edit" && formState.row) {
        await updateCalidadObjetivo(formState.row.id, payload);
      } else {
        await createCalidadObjetivo(payload);
      }
      setFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar el objetivo."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEtapa = async (values: CalidadObjetivoEtapaFormValues) => {
    if (!etapaFormState.open) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const payload = mapEtapaFormToPayload(values);
      if (etapaFormState.mode === "edit" && etapaFormState.etapa) {
        await updateObjetivoEtapa(etapaFormState.objetivo.id, etapaFormState.etapa.id, payload);
      } else {
        await createObjetivoEtapa(etapaFormState.objetivo.id, payload);
      }
      setEtapaFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(getRequestErrorMessage(requestError, "No se pudo guardar la etapa."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.open) return;

    setSubmitting(true);
    try {
      if (deleteState.type === "objetivo") {
        await deleteCalidadObjetivo(deleteState.objetivo.id);
        if (fichaObjetivoId === deleteState.objetivo.id) setFichaObjetivoId(null);
      } else if (deleteState.type === "etapa" && deleteState.etapa) {
        await deleteObjetivoEtapa(deleteState.objetivo.id, deleteState.etapa.id);
      } else if (deleteState.type === "anexo" && deleteState.anexoId) {
        await deleteObjetivoAnexo(deleteState.objetivo.id, deleteState.anexoId);
      }
      setDeleteState({ open: false });
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (objetivo: CalidadObjetivo, files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploadingFor(objetivo.id);
    try {
      await uploadObjetivoAnexos(objetivo.id, files);
      await reload();
    } finally {
      setUploadingFor(null);
    }
  };

  return (
    <section className="calidad-section objetivos-section">
      <Iso900114001SectionHeader
        eyebrow="Objetivos anuales"
        title="Objetivos"
        description="Registro de objetivos de calidad y medioambiente con etapas, seguimiento, cierre, firma y anexos."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard
          icon={Target}
          label="Objetivos"
          value={loading ? "..." : meta.total}
        />
        <Iso900114001OverviewCard
          icon={Clock3}
          label="Abiertos"
          value={loading ? "..." : overviewCounts.abiertos}
        />
        <Iso900114001OverviewCard
          icon={CheckCircle2}
          label="Cerrados"
          value={loading ? "..." : overviewCounts.cerrados}
        />
      </div>

      {fichaObjetivoId !== null ? (
        <div className="objetivos-ficha-page">
          <ObjetivoFicha
            objetivo={fichaObjetivo}
            uploading={uploadingFor === fichaObjetivo?.id}
            onBackToList={() => setFichaObjetivoId(null)}
            onCreateEtapa={(objetivo) => {
              setFormError(null);
              setEtapaFormState({ open: true, mode: "create", objetivo, etapa: null });
            }}
            onEditEtapa={(objetivo, etapa) => {
              setFormError(null);
              setEtapaFormState({ open: true, mode: "edit", objetivo, etapa });
            }}
            onDeleteEtapa={(objetivo, etapa) => setDeleteState({ open: true, type: "etapa", objetivo, etapa, title: "Eliminar etapa", description: `Se eliminará la etapa ${etapa.orden}.` })}
            onUpload={handleUpload}
            onDeleteAnexo={(objetivo, anexoId, name) => setDeleteState({ open: true, type: "anexo", objetivo, anexoId, title: "Eliminar anexo", description: `Se eliminará el anexo ${name}.` })}
          />
        </div>
      ) : (
        <div className="objetivos-layout objetivos-list-layout">
          <section className="objetivos-panel objetivos-main-panel">
            <div className="objetivos-toolbar">
              <div>
                <div className="calidad-toolbar-eyebrow">Tabla principal</div>
                <h3 className="calidad-toolbar-title">Objetivos registrados</h3>
              </div>
              <div className="calidad-toolbar-meta">
                <span className="calidad-toolbar-count">{loading ? "Cargando..." : `${meta.total} registros`}</span>
                <button type="button" onClick={() => void openCreate()} className="calidad-primary-button">
                  <Plus size={16} />
                  Nuevo objetivo
                </button>
              </div>
            </div>

            <div className="objetivos-filters">
              <label className="objetivos-filter-field">
                <span>Año</span>
                <select value={filters.anio} onChange={(event) => setFilters((current) => ({ ...current, anio: event.target.value }))}>
                  <option value="">Todos</option>
                  {years.map((year) => (
                    <option key={year} value={String(year)}>{year}</option>
                  ))}
                </select>
              </label>
              <label className="objetivos-filter-field objetivos-filter-search">
                <span>Buscar</span>
                <input
                  value={filters.search}
                  onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                  placeholder="Nº objetivo, definición u observaciones"
                />
              </label>
              <label className="objetivos-filter-field">
                <span>Cerrado</span>
                <select value={filters.cerrado} onChange={(event) => setFilters((current) => ({ ...current, cerrado: event.target.value as CalidadObjetivoFilters["cerrado"] }))}>
                  <option value="">Todos</option>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              </label>
              <button type="button" onClick={() => setFilters(defaultObjetivoFilters)} className="calidad-toolbar-reset">
                Limpiar filtros
              </button>
            </div>

            {error ? (
              <Feedback title="No se pudo cargar objetivos" copy={error} onRetry={() => void reload()} />
            ) : loading ? (
              <LoadingState title="Cargando objetivos" />
            ) : objetivos.length === 0 ? (
              <div className="calidad-feedback-state">
                <div className="calidad-feedback-title">No hay objetivos con los filtros actuales</div>
                <p className="calidad-feedback-copy">Crea el primer objetivo o limpia los filtros.</p>
                <button type="button" onClick={() => void openCreate()} className="calidad-primary-button"><Plus size={16} /> Nuevo objetivo</button>
              </div>
            ) : (
              <div className="objetivos-table-scroll">
                <table className="calidad-table objetivos-table min-w-full">
                  <thead>
                    <tr>
                      <th className="objetivos-actions-col">Acciones</th>
                      <th>Año</th>
                      <th>Nº objetivo</th>
                      <th>Definición</th>
                      <th>Cerrado</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {objetivos.map((objetivo) => (
                      <tr key={objetivo.id}>
                        <td className="objetivos-actions-col">
                          <div className="calidad-table-actions-row">
                            <button type="button" onClick={() => setFichaObjetivoId(objetivo.id)} className="calidad-icon-button" title="Ver ficha"><Eye size={15} /></button>
                            <button type="button" onClick={() => { setFormError(null); setFormState({ open: true, mode: "edit", row: objetivo }); }} className="calidad-icon-button" title="Editar"><Edit3 size={15} /></button>
                            <button type="button" onClick={() => setDeleteState({ open: true, type: "objetivo", objetivo, title: "Eliminar objetivo", description: `Se eliminará el objetivo ${objetivo.numero_objetivo}, sus etapas, seguimiento y anexos.` })} className="calidad-icon-button danger" title="Eliminar"><Trash2 size={15} /></button>
                          </div>
                        </td>
                        <td>{objetivo.anio}</td>
                        <td className="objetivos-number-col">{objetivo.numero_objetivo}</td>
                        <td className="objetivos-definition-col">{objetivo.definicion}</td>
                        <td><span className={objetivo.cerrado ? "objetivos-status closed" : "objetivos-status open"}>{objetivo.cerrado ? "Sí" : "No"}</span></td>
                        <td>{objetivo.observaciones || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      <ObjetivoFormModal
        state={formState}
        submitting={submitting}
        error={formError}
        cloneSources={cloneSources}
        cloneLoading={cloneLoading}
        defaultAnio={Number.parseInt(filters.anio || String(new Date().getFullYear()), 10)}
        onTargetYearChange={(targetYear) => void loadCloneSourcesForYear(targetYear)}
        onClose={() => setFormState({ open: false })}
        onSubmit={(values) => void handleSubmitObjetivo(values)}
      />

      <EtapaFormModal
        state={etapaFormState}
        submitting={submitting}
        error={formError}
        onClose={() => setEtapaFormState({ open: false })}
        onSubmit={(values) => void handleSubmitEtapa(values)}
      />

      <Iso900114001ConfirmDialog
        open={deleteState.open}
        title={deleteState.open ? deleteState.title : "Eliminar registro"}
        description={deleteState.open ? deleteState.description : ""}
        confirming={submitting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={() => void handleDelete()}
      />
    </section>
  );
}

function ObjetivoFicha({
  objetivo,
  uploading,
  onBackToList,
  onCreateEtapa,
  onEditEtapa,
  onDeleteEtapa,
  onUpload,
  onDeleteAnexo,
}: {
  objetivo: CalidadObjetivo | null;
  uploading: boolean;
  onBackToList: () => void;
  onCreateEtapa: (objetivo: CalidadObjetivo) => void;
  onEditEtapa: (objetivo: CalidadObjetivo, etapa: CalidadObjetivoEtapa) => void;
  onDeleteEtapa: (objetivo: CalidadObjetivo, etapa: CalidadObjetivoEtapa) => void;
  onUpload: (objetivo: CalidadObjetivo, files: FileList | null) => void;
  onDeleteAnexo: (objetivo: CalidadObjetivo, anexoId: number, name: string) => void;
}) {
  if (!objetivo) {
    return (
      <section className="objetivos-panel objetivos-detail-panel">
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">Ficha no disponible</div>
          <p className="calidad-feedback-copy">El objetivo no está en la lista cargada. Vuelve al listado para seleccionarlo de nuevo.</p>
          <button type="button" onClick={onBackToList} className="calidad-secondary-button"><ArrowLeft size={15} /> Volver al listado</button>
        </div>
      </section>
    );
  }

  return (
    <section className="objetivos-panel objetivos-detail-panel">
      <div className="objetivos-ficha-header">
        <div>
          <div className="calidad-toolbar-eyebrow">Ficha del objetivo</div>
          <h3 className="calidad-toolbar-title">{objetivo.numero_objetivo}</h3>
          <p className="calidad-toolbar-copy">Año {objetivo.anio}</p>
        </div>
        <div className="objetivos-ficha-header-actions">
          <button type="button" onClick={onBackToList} className="calidad-secondary-button"><ArrowLeft size={15} /> Volver al listado</button>
          <span className={objetivo.cerrado ? "objetivos-status closed" : "objetivos-status open"}>{objetivo.cerrado ? "Cerrado" : "Abierto"}</span>
        </div>
      </div>

      <div className="objetivos-ficha-block">
        <h4>Definición</h4>
        <p>{objetivo.definicion}</p>
      </div>

      <div className="objetivos-ficha-block">
        <div className="objetivos-block-heading">
          <h4>Etapas del objetivo</h4>
          <button type="button" onClick={() => onCreateEtapa(objetivo)} className="calidad-secondary-button" title="Añadir etapa"><Plus size={15} /> Añadir etapa</button>
        </div>
        {objetivo.etapas.length === 0 ? (
          <p className="objetivos-muted">No hay etapas registradas.</p>
        ) : (
          <div className="objetivos-etapas-list">
            {objetivo.etapas.map((etapa) => (
              <article key={etapa.id} className="objetivos-etapa-card">
                <div className="objetivos-etapa-topline">
                  <strong>Etapa {etapa.orden}</strong>
                  <div className="calidad-table-actions-row">
                    <button type="button" onClick={() => onEditEtapa(objetivo, etapa)} className="calidad-icon-button"><Edit3 size={14} /></button>
                    <button type="button" onClick={() => onDeleteEtapa(objetivo, etapa)} className="calidad-icon-button danger"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p>{etapa.descripcion}</p>
                <dl>
                  <div><dt>Responsable ejecución</dt><dd>{etapa.responsable_ejecucion || "—"}</dd></div>
                  <div><dt>Coste</dt><dd>{formatCurrency(etapa.coste)}</dd></div>
                  <div><dt>Esfuerzo</dt><dd>{etapa.esfuerzo || "—"}</dd></div>
                  <div><dt>Responsable seguimiento</dt><dd>{etapa.responsable_seguimiento || "—"}</dd></div>
                  <div><dt>Plazo</dt><dd>{etapa.plazo || "—"}</dd></div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </div>

      <div className="objetivos-ficha-block">
        <h4>Seguimiento</h4>
        <p>{objetivo.seguimiento?.seguimiento || "Sin seguimiento registrado."}</p>
      </div>

      <div className="objetivos-ficha-grid">
        <div className="objetivos-ficha-block compact">
          <h4>Firma</h4>
          <p>{objetivo.seguimiento?.firma ? "Firmado" : "Pendiente"}</p>
          {objetivo.seguimiento?.firma_fecha ? <small>{new Date(objetivo.seguimiento.firma_fecha).toLocaleString()}</small> : null}
        </div>
        <div className="objetivos-ficha-block compact">
          <h4>Cierre del objetivo</h4>
          <p>{objetivo.seguimiento?.cierre_objetivo || "Sin cierre registrado."}</p>
        </div>
      </div>

      <div className="objetivos-ficha-block">
        <div className="objetivos-block-heading">
          <h4>Anexos</h4>
          <label className="calidad-secondary-button objetivos-upload-button">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />}
            Subir anexo
            <input type="file" multiple onChange={(event) => onUpload(objetivo, event.target.files)} />
          </label>
        </div>
        {objetivo.anexos.length === 0 ? (
          <p className="objetivos-muted">No hay anexos.</p>
        ) : (
          <div className="objetivos-anexos-list">
            {objetivo.anexos.map((anexo) => (
              <div key={anexo.id} className="objetivos-anexo-row">
                <span>{anexo.archivo_nombre}</span>
                <div className="calidad-table-actions-row">
                  <a href={objetivoAnexoDownloadUrl(objetivo.id, anexo.id)} className="calidad-icon-button" title="Descargar"><Download size={14} /></a>
                  <button type="button" onClick={() => onDeleteAnexo(objetivo, anexo.id, anexo.archivo_nombre)} className="calidad-icon-button danger"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ObjetivoFormModal({
  state,
  submitting,
  error,
  cloneSources,
  cloneLoading,
  defaultAnio,
  onTargetYearChange,
  onClose,
  onSubmit,
}: {
  state: ObjetivoFormState;
  submitting: boolean;
  error: string | null;
  cloneSources: CalidadObjetivoCloneSource[];
  cloneLoading: boolean;
  defaultAnio: number;
  onTargetYearChange: (targetYear: number) => void;
  onClose: () => void;
  onSubmit: (values: CalidadObjetivoFormValues) => void;
}) {
  const [values, setValues] = useState<CalidadObjetivoFormValues>(
    state.open && state.mode === "edit" && state.row ? mapObjetivoToForm(state.row) : createEmptyObjetivoForm(defaultAnio),
  );
  const [selectedCloneId, setSelectedCloneId] = useState("");

  useEffect(() => {
    if (!state.open) return;
    setValues(state.mode === "edit" && state.row ? mapObjetivoToForm(state.row) : createEmptyObjetivoForm(defaultAnio));
    setSelectedCloneId("");
  }, [state, defaultAnio]);

  if (!state.open) return null;

  const setPatch = (patch: Partial<CalidadObjetivoFormValues>) => setValues((current) => ({ ...current, ...patch }));

  return (
    <div className="calidad-modal-backdrop" role="presentation">
      <div className="calidad-modal objetivos-modal" role="dialog" aria-modal="true">
        <ModalHeader title={state.mode === "edit" ? "Editar objetivo" : "Nuevo objetivo"} onClose={onClose} />
        <form className="objetivos-form" onSubmit={(event) => { event.preventDefault(); onSubmit(values); }}>
          {state.mode === "create" ? (
            <label className="objetivos-form-field full">
              <span>Copiar datos del año anterior</span>
              <select
                value={selectedCloneId}
                disabled={cloneLoading || cloneSources.length === 0}
                onChange={(event) => {
                  setSelectedCloneId(event.target.value);
                  const selected = cloneSources.find((source) => String(source.source_id) === event.target.value);
                  if (selected) setValues(mapCloneSourceToForm(selected));
                }}
              >
                <option value="">{cloneLoading ? "Buscando objetivos del año anterior..." : "Crear vacío"}</option>
                {cloneSources.map((source) => (
                  <option key={source.source_id} value={source.source_id}>Copiar {source.numero_objetivo} de {source.source_anio}</option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="objetivos-form-grid">
            <label className="objetivos-form-field"><span>Año</span><input required type="number" min="2000" max="2100" value={values.anio} onChange={(event) => {
              const nextYear = event.target.value;
              setPatch({ anio: nextYear });
              setSelectedCloneId("");
              if (state.mode === "create") {
                const parsedYear = Number.parseInt(nextYear, 10);
                if (!Number.isNaN(parsedYear)) onTargetYearChange(parsedYear);
              }
            }} /></label>
            <label className="objetivos-form-field"><span>Nº objetivo</span><input required value={values.numero_objetivo} onChange={(event) => setPatch({ numero_objetivo: event.target.value })} /></label>
            <label className="objetivos-form-field checkbox"><input type="checkbox" checked={values.cerrado} onChange={(event) => setPatch({ cerrado: event.target.checked })} /><span>Cerrado</span></label>
            <label className="objetivos-form-field full"><span>Definición</span><textarea required rows={3} value={values.definicion} onChange={(event) => setPatch({ definicion: event.target.value })} /></label>
            <label className="objetivos-form-field full"><span>Observaciones</span><textarea rows={2} value={values.observaciones} onChange={(event) => setPatch({ observaciones: event.target.value })} /></label>
            <label className="objetivos-form-field full"><span>Seguimiento</span><textarea rows={4} value={values.seguimiento} onChange={(event) => setPatch({ seguimiento: event.target.value })} /></label>
            <label className="objetivos-form-field checkbox"><input type="checkbox" checked={values.firma} onChange={(event) => setPatch({ firma: event.target.checked })} /><span>Firma</span></label>
            <label className="objetivos-form-field full"><span>Cierre del objetivo</span><textarea rows={4} value={values.cierre_objetivo} onChange={(event) => setPatch({ cierre_objetivo: event.target.value })} /></label>
          </div>

          {error ? <div className="calidad-modal-error">{error}</div> : null}
          <ModalActions submitting={submitting} onClose={onClose} submitLabel="Guardar objetivo" />
        </form>
      </div>
    </div>
  );
}

function EtapaFormModal({
  state,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  state: EtapaFormState;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (values: CalidadObjetivoEtapaFormValues) => void;
}) {
  const [values, setValues] = useState<CalidadObjetivoEtapaFormValues>(
    state.open && state.mode === "edit" && state.etapa
      ? mapEtapaToForm(state.etapa)
      : createEmptyEtapaForm(state.open ? state.objetivo.etapas.length + 1 : 1),
  );

  useEffect(() => {
    if (!state.open) return;
    setValues(state.mode === "edit" && state.etapa ? mapEtapaToForm(state.etapa) : createEmptyEtapaForm(state.objetivo.etapas.length + 1));
  }, [state]);

  if (!state.open) return null;

  const setPatch = (patch: Partial<CalidadObjetivoEtapaFormValues>) => setValues((current) => ({ ...current, ...patch }));

  return (
    <div className="calidad-modal-backdrop" role="presentation">
      <div className="calidad-modal objetivos-modal" role="dialog" aria-modal="true">
        <ModalHeader title={state.mode === "edit" ? "Editar etapa" : "Nueva etapa"} onClose={onClose} />
        <form className="objetivos-form" onSubmit={(event) => { event.preventDefault(); onSubmit(values); }}>
          <div className="objetivos-form-grid">
            <label className="objetivos-form-field"><span>Nº orden</span><input required type="number" min="1" value={values.orden} onChange={(event) => setPatch({ orden: event.target.value })} /></label>
            <label className="objetivos-form-field"><span>Coste</span><input required type="number" min="0" step="0.01" value={values.coste} onChange={(event) => setPatch({ coste: event.target.value })} /></label>
            <label className="objetivos-form-field full"><span>Descripción</span><textarea required rows={4} value={values.descripcion} onChange={(event) => setPatch({ descripcion: event.target.value })} /></label>
            <label className="objetivos-form-field"><span>Responsable ejecución</span><input value={values.responsable_ejecucion} onChange={(event) => setPatch({ responsable_ejecucion: event.target.value })} /></label>
            <label className="objetivos-form-field"><span>Esfuerzo</span><input value={values.esfuerzo} onChange={(event) => setPatch({ esfuerzo: event.target.value })} /></label>
            <label className="objetivos-form-field"><span>Responsable seguimiento</span><input value={values.responsable_seguimiento} onChange={(event) => setPatch({ responsable_seguimiento: event.target.value })} /></label>
            <label className="objetivos-form-field"><span>Plazo</span><input value={values.plazo} onChange={(event) => setPatch({ plazo: event.target.value })} /></label>
          </div>
          {error ? <div className="calidad-modal-error">{error}</div> : null}
          <ModalActions submitting={submitting} onClose={onClose} submitLabel="Guardar etapa" />
        </form>
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="calidad-modal-header">
      <h3>{title}</h3>
      <button type="button" onClick={onClose} className="calidad-modal-close"><X size={18} /></button>
    </div>
  );
}

function ModalActions({ submitting, onClose, submitLabel }: { submitting: boolean; onClose: () => void; submitLabel: string }) {
  return (
    <div className="calidad-modal-actions">
      <button type="button" onClick={onClose} className="calidad-secondary-button" disabled={submitting}>Cancelar</button>
      <button type="submit" className="calidad-primary-button" disabled={submitting}>{submitting ? "Guardando..." : submitLabel}</button>
    </div>
  );
}

function Feedback({ title, copy, onRetry }: { title: string; copy: string; onRetry: () => void }) {
  return (
    <div className="calidad-feedback-state">
      <div className="calidad-feedback-title">{title}</div>
      <p className="calidad-feedback-copy">{copy}</p>
      <button type="button" onClick={onRetry} className="calidad-feedback-action"><RefreshCw size={15} /> Reintentar</button>
    </div>
  );
}

function LoadingState({ title }: { title: string }) {
  return (
    <div className="calidad-feedback-state">
      <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
      <div className="calidad-feedback-title">{title}</div>
    </div>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(value || 0);
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError(error)) return fallback;
  const fieldErrors = error.response?.data?.errors;
  const firstError = fieldErrors && typeof fieldErrors === "object" ? Object.values(fieldErrors).find(Array.isArray)?.[0] : null;
  if (typeof firstError === "string") return firstError;
  const message = error.response?.data?.message;
  return typeof message === "string" ? message : fallback;
}
